import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TitleStrategy } from '@angular/router';
import { ToastrModule } from 'ngx-toastr';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AppTitleStrategy } from './app-title.strategy';
import { BarSelectComponent } from './components/bar-select/bar-select.component';
import { ContractSelectComponent } from './components/contract-select/contract-select.component';
import { DetailPayneComponent } from './components/detail-payne/detail-payne.component';
import { GridSwitchComponent } from './components/grid-switch/grid-switch.component';
import { ModalComponent } from './components/modal/modal.component';
import { ModalContentAboutComponent } from './components/modal/modal-content-about/modal-content-about.component';
import { ModalContentApproverSelectComponent } from './components/modal/modal-content-approver-select/modal-content-approver-select.component';
import { ModalContentBillCodeChangeComponent } from './components/modal/modal-content-bill-code-change/modal-content-bill-code-change.component';
import { ModalContentCatsInfoComponent } from './components/modal/modal-content-cats-info/modal-content-cats-info.component';
import { ModalContentConfirmComponent } from './components/modal/modal-content-confirm/modal-content-confirm.component';
import { ModalContentContractSelectComponent } from './components/modal/modal-content-contract-select/modal-content-contract-select.component';
import { ModalContentDefaultChangeComponent } from './components/modal/modal-content-default-change/modal-content-default-change.component';
import { ModalContentEntityNumberLookupComponent } from './components/modal/modal-content-entity-number-lookup/modal-content-entity-number-lookup.component';
import { ModalContentNeedHelpComponent } from './components/modal/modal-content-need-help/modal-content-need-help.component';
import { ModalContentNonbillableSelectComponent } from './components/modal/modal-content-nonbillable-select/modal-content-nonbillable-select.component';
import { ModalContentPerDiemTotalsComponent } from './components/modal/modal-content-per-diem-totals/modal-content-per-diem-totals.component';
import { ModalContentSimpleTextComponent } from './components/modal/modal-content-simple-text/modal-content-simple-text.component';
import { ModalContentSplitModalComponent } from './components/modal/modal-content-split-modal/modal-content-split-modal.component';
import { ModalContentTimeEvalComponent } from './components/modal/modal-content-time-eval/modal-content-time-eval.component';
import { ModalContentTimesheetCreateComponent } from './components/modal/modal-content-timesheet-create-or-edit/modal-content-timesheet-create-or-edit.component';
import { ModalContentTimesheetMoveComponent } from './components/modal/modal-content-timesheet-move/modal-content-timesheet-move.component';
import { ModalContentTimesheetSelectComponent } from './components/modal/modal-content-timesheet-select/modal-content-timesheet-select.component';
import { ModalContentVerticalBulkAddPerDiemComponent } from './components/modal/modal-content-vertical-bulk-add-per-diem/modal-content-vertical-bulk-add-per-diem.component';
import { PayrollDayGridComponent } from './components/payroll-day-grid/payroll-day-grid.component';
import { SingleFileUploadComponent } from './components/single-file-upload/single-file-upload.component';
import { TableLikeLayoutComponent } from './components/table-like-layout/table-like-layout.component';
import { TabsComponent } from './components/tabs/tabs.component';
import { TimesheetHeadersComponent } from './components/timesheet-headers/timesheet-headers.component';
import { ToastComponent } from './components/toast/toast.component';
import { WedateInputComponent } from './components/wedate-input/wedate-input.component';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import { SetLoggingHeadersInterceptor } from './interceptors/set-logging-headers.interceptor';
import { MaterialModule } from './material.module';
import { BillingComponent } from './pages/billing/billing.component';
import { BillingReviewComponent } from './pages/billing-review/billing-review.component';
import { HomeComponent } from './pages/home/home.component';
import { PayrollOverviewComponent } from './pages/payroll-overview/payroll-overview.component';
import { TimesheetManagerComponent } from './pages/timesheet-manager/timesheet-manager.component';
import { HideZeroesPipe } from './pipes/hide-zeroes.pipe';
import { StatusPipe } from './pipes/status.pipe';
import { TrimDecimalPipe } from './pipes/trim-decimal.pipe';

@NgModule({
  declarations: [
    //Try to keep this array alphabetized!
    AppComponent,
    BarSelectComponent,
    BillingComponent,
    BillingReviewComponent,
    ContractSelectComponent,
    DetailPayneComponent,
    GridSwitchComponent,
    HideZeroesPipe,
    HomeComponent,
    ModalComponent,
    ModalContentAboutComponent,
    ModalContentApproverSelectComponent,
    ModalContentBillCodeChangeComponent,
    ModalContentCatsInfoComponent,
    ModalContentConfirmComponent,
    ModalContentContractSelectComponent,
    ModalContentDefaultChangeComponent,
    ModalContentEntityNumberLookupComponent,
    ModalContentNeedHelpComponent,
    ModalContentNonbillableSelectComponent,
    ModalContentPerDiemTotalsComponent,
    ModalContentSimpleTextComponent,
    ModalContentSplitModalComponent,
    ModalContentTimeEvalComponent,
    ModalContentTimesheetCreateComponent,
    ModalContentTimesheetMoveComponent,
    ModalContentTimesheetSelectComponent,
    ModalContentVerticalBulkAddPerDiemComponent,
    PayrollDayGridComponent,
    PayrollOverviewComponent,
    SingleFileUploadComponent,
    StatusPipe,
    TableLikeLayoutComponent,
    TabsComponent,
    TimesheetHeadersComponent,
    TimesheetManagerComponent,
    ToastComponent,
    TrimDecimalPipe,
    WedateInputComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    FormsModule,
    MaterialModule,
    NgOptimizedImage,
    ReactiveFormsModule,
    ScrollingModule,
    ToastrModule.forRoot({
      // Toastr: https://github.com/scttcper/ngx-toastr
      toastComponent: ToastComponent,
      positionClass: 'toast-bottom-right',
      closeButton: true,
      tapToDismiss: false,
      progressBar: true,
      preventDuplicates: true,
      disableTimeOut: false, //mouse over to stop the timer
      resetTimeoutOnDuplicate: true,
      enableHtml: true,
      timeOut: 8000,
      extendedTimeOut: 8000, //reset the timer back to the beginning after mousing over it
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SetLoggingHeadersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: TitleStrategy,
      useClass: AppTitleStrategy,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- This rule is OK to disable here since a class is required and we don't have to do anything with it
export class AppModule {}
